import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { Bell, LogOut, GraduationCap, BookOpen, Users, Video, LayoutDashboard, PlusCircle, Calendar, ChevronRight, Package } from "lucide-react";

const roleLinks = {
  learner: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/catalog", label: "Course Catalog", icon: BookOpen },
    { to: "/bundles", label: "Bundles", icon: Package },
    { to: "/webinars", label: "Webinars", icon: Calendar },
    { to: "/my-courses", label: "My Learning", icon: GraduationCap },
  ],
  teacher: [
    { to: "/dashboard", label: "Studio", icon: LayoutDashboard },
    { to: "/teacher/courses", label: "My Courses", icon: BookOpen },
    { to: "/teacher/new-course", label: "Create Course", icon: PlusCircle },
    { to: "/webinars", label: "My Webinars", icon: Calendar },
    { to: "/teacher/new-webinar", label: "Plan Webinar", icon: PlusCircle },
  ],
  admin: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/courses", label: "Courses", icon: BookOpen },
    { to: "/admin/webinars", label: "Webinars", icon: Calendar },
    { to: "/admin/bundles", label: "Bundles", icon: Package },
    { to: "/admin/videos", label: "Videos", icon: Video },
  ],
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const links = roleLinks[user?.role] || roleLinks.learner;

  return (
    <div className="min-h-screen flex bg-[#F6F5F2]">
      {/* Sidebar */}
      <aside className="sidebar-dark hidden md:flex flex-col w-64 p-6 fixed h-screen" data-testid="sidebar">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-8 h-8 rounded-md bg-[#D45D3C] flex items-center justify-center">
            <GraduationCap size={18} className="text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-white leading-none">SkillCrew</div>
            <div className="overline text-white/50">Learning Studio</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1 flex-1">
          {links.map((l) => {
            const Icon = l.icon;
            const active = location.pathname === l.to || (l.to !== "/dashboard" && location.pathname.startsWith(l.to));
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${active ? "active" : "hover:bg-white/5"}`}
              >
                <Icon size={16} />
                <span>{l.label}</span>
                {active && <ChevronRight size={14} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <img src={user?.picture || `https://i.pravatar.cc/40?u=${user?.email}`} alt="" className="w-9 h-9 rounded-full object-cover" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate text-white">{user?.name || "User"}</div>
              <div className="overline text-white/50">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E5E3D8]">
          <div className="flex items-center justify-between px-6 md:px-10 h-16">
            <div className="md:hidden font-heading text-lg font-semibold">SkillCrew</div>
            <div className="hidden md:block">
              <div className="overline">Signed in as</div>
              <div className="font-heading text-base font-medium capitalize">{user?.role} · {user?.name}</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/notifications")}
                data-testid="notifications-btn"
                className="relative p-2 rounded-md hover:bg-[#F1EFE7]"
                aria-label="notifications"
              >
                <Bell size={18} />
              </button>
              <img src={user?.picture || `https://i.pravatar.cc/40?u=${user?.email}`} alt="" className="w-9 h-9 rounded-full object-cover md:hidden" />
            </div>
          </div>
        </header>

        <main className="px-6 md:px-10 py-8 fade-up" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
